import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonInput,
  IonInputPasswordToggle,
  IonItem,
  ToastController,
} from '@ionic/angular';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { firstValueFrom } from 'rxjs';
import { map, merge, startWith } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import {
  ChangePasswordErrorKey,
  IdentityService,
  mapChangePasswordError,
  MIN_PASSWORD_LENGTH,
} from '../../../services/identity.service';
import { PanelHostService } from '../../../services/panel-host.service';
import { ButtonComponent } from '../../ui/button/button.component';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { InlineAlertComponent } from '../../ui/inline-alert/inline-alert.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-change-password-panel',
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    ButtonComponent,
    ContentContainerComponent,
    InlineAlertComponent,
    PanelBodyComponent,
    IonInput,
    IonInputPasswordToggle,
    IonItem,
  ],
  templateUrl: './change-password-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordPanelComponent implements OnDestroy {
  private readonly panelHost = inject(PanelHostService);
  private readonly identityService = inject(IdentityService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly formBuilder = inject(FormBuilder);
  readonly authService = inject(AuthService);

  readonly submitting = signal(false);
  readonly errorMessage = signal<ChangePasswordErrorKey | 'password-mismatch' | null>(null);
  readonly submitAttempted = signal(false);

  readonly minPasswordLength = MIN_PASSWORD_LENGTH;

  readonly form = this.formBuilder.nonNullable.group({
    currentPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]],
    confirmPassword: ['', Validators.required],
  });

  private readonly formInvalid = toSignal(
    merge(this.form.statusChanges, this.form.valueChanges).pipe(
      startWith(null),
      map(() => this.form.invalid),
    ),
    { initialValue: this.form.invalid },
  );

  readonly showValidationErrors = computed(
    () => this.submitAttempted() && this.formInvalid(),
  );

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() !== 'change-password') {
        return;
      }

      this.panelHost.registerDirtyPredicate(() => this.form.dirty && !this.submitting());

      this.errorMessage.set(null);
      this.submitAttempted.set(false);
      this.form.reset({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    });
  }

  ngOnDestroy(): void {
    this.panelHost.clearDirtyPredicate();
  }

  cancel(): void {
    void this.panelHost.dismiss('/profile');
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    this.errorMessage.set(null);
    this.submitAttempted.set(true);

    const { newPassword, confirmPassword } = this.form.getRawValue();
    if (newPassword !== confirmPassword) {
      this.errorMessage.set('password-mismatch');
      return;
    }

    if (this.form.invalid || this.submitting()) {
      return;
    }

    this.submitAttempted.set(false);
    this.submitting.set(true);

    try {
      await firstValueFrom(
        this.identityService.changePassword(
          this.form.controls.currentPassword.value,
          newPassword,
        ),
      );

      this.form.markAsPristine();
      const toast = await this.toastController.create({
        message: this.transloco.translate(marker('identity.change-password.success')),
        duration: 4000,
        color: 'success',
      });
      await toast.present();
      void this.panelHost.dismiss('/profile');
    } catch (error) {
      this.errorMessage.set(mapChangePasswordError(error));
    } finally {
      this.submitting.set(false);
    }
  }
}
